import { useMemo, useState } from "react";
import type { CatalogItemView } from "../../data/local/catalog";
import type { LocalModifier, LocalVariant } from "../../data/local/db";
import { formatMoney, type CurrencyFormat } from "../../domain/money/format";
import { add, sum, type Money } from "../../domain/money/money";
import { Slot } from "../slots/Slot";

/**
 * Item configuration: variant, modifiers, and a note.
 *
 * Min/max selection rules are enforced here (FR-003) rather than trusted to the
 * staff member — a group with max 1 behaves as a radio, one with max n stops
 * accepting at n, and a required group blocks the add until satisfied.
 */

interface ItemSheetProps {
  view: CatalogItemView;
  currency: CurrencyFormat;
  onAdd(view: CatalogItemView, variant: LocalVariant, modifiers: LocalModifier[]): void;
  onCancel(): void;
}

export function ItemSheet({ view, currency, onAdd, onCancel }: ItemSheetProps) {
  const [variant, setVariant] = useState<LocalVariant | null>(view.defaultVariant);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (group: { id: string; maxSelections: number }, modifier: LocalModifier) => {
    setSelected((current) => {
      const next = new Set(current);
      const groupModifierIds = view.modifierGroups
        .find((g) => g.group.id === group.id)!
        .modifiers.map((m) => m.id);

      if (next.has(modifier.id)) {
        next.delete(modifier.id);
        return next;
      }

      // A single-select group swaps rather than accumulating.
      if (group.maxSelections === 1) {
        for (const id of groupModifierIds) next.delete(id);
        next.add(modifier.id);
        return next;
      }

      const chosenInGroup = groupModifierIds.filter((id) => next.has(id)).length;
      if (chosenInGroup >= group.maxSelections) return current;

      next.add(modifier.id);
      return next;
    });
  };

  const chosenModifiers = useMemo(
    () =>
      view.modifierGroups.flatMap(({ modifiers }) => modifiers.filter((m) => selected.has(m.id))),
    [view.modifierGroups, selected],
  );

  const unmetGroups = view.modifierGroups.filter(({ group, modifiers }) => {
    const chosen = modifiers.filter((m) => selected.has(m.id)).length;
    return chosen < group.minSelections || (group.required && chosen === 0);
  });

  const canAdd = variant !== null && unmetGroups.length === 0;

  const runningTotal: Money = variant
    ? add(variant.price, sum(chosenModifiers.map((m) => m.priceAdjustment)))
    : (0 as Money);

  return (
    <div className="sheet" role="dialog" aria-label={view.item.name}>
      <header className="sheet__head">
        <h2 className="sheet__title">{view.item.name}</h2>
        {view.item.description && <p className="sheet__desc">{view.item.description}</p>}
        <Slot id="till.item.detail" itemId={view.item.id} variantId={variant?.id ?? null} />
      </header>

      <div className="sheet__body">
        {view.variants.length > 1 && (
          <section className="sheet__group">
            <h3 className="sheet__group-title">Size</h3>
            <div className="sheet__options">
              {view.variants.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`sheet__option${variant?.id === option.id ? " is-selected" : ""}`}
                  onClick={() => setVariant(option)}
                  aria-pressed={variant?.id === option.id}
                >
                  <span>{option.name}</span>
                  <span className="money">{formatMoney(option.price, currency)}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {view.modifierGroups.map(({ group, modifiers }) => {
          const chosen = modifiers.filter((m) => selected.has(m.id)).length;
          const atLimit = chosen >= group.maxSelections;

          return (
            <section key={group.id} className="sheet__group">
              <h3 className="sheet__group-title">
                {group.name}
                <span className="sheet__group-rule">
                  {group.required || group.minSelections > 0
                    ? `choose ${group.minSelections || 1}`
                    : `up to ${group.maxSelections}`}
                </span>
              </h3>
              <div className="sheet__options">
                {modifiers.map((modifier) => {
                  const isSelected = selected.has(modifier.id);
                  return (
                    <button
                      key={modifier.id}
                      type="button"
                      className={`sheet__option${isSelected ? " is-selected" : ""}`}
                      disabled={!isSelected && atLimit && group.maxSelections > 1}
                      onClick={() => toggle(group, modifier)}
                      aria-pressed={isSelected}
                    >
                      <span>{modifier.name}</span>
                      {modifier.priceAdjustment !== 0 && (
                        <span className="money">
                          {modifier.priceAdjustment > 0 ? "+" : ""}
                          {formatMoney(modifier.priceAdjustment, currency)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <footer className="sheet__foot">
        <button type="button" className="sheet__cancel" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="sheet__add"
          disabled={!canAdd}
          onClick={() => variant && onAdd(view, variant, chosenModifiers)}
        >
          Add <span className="money">{formatMoney(runningTotal, currency)}</span>
        </button>
      </footer>
    </div>
  );
}

export default ItemSheet;
